/**
 * Retry ownership policy for ORIGINAL_ENGINE_COMPAT transport stack.
 *
 * One original logical attempt → one authenticated HTTP request → one upstream Gemini call.
 * Golden logical retries (max 5) own all retry decisions; transport layers do not multiply.
 */
export const ORIGINAL_COMPAT_CLIENT_HTTP_ATTEMPTS_PER_LOGICAL_CALL = 1;
export const ORIGINAL_COMPAT_SERVER_UPSTREAM_ATTEMPTS_PER_LOGICAL_CALL = 1;
export const GOLDEN_LOGICAL_MAX_ATTEMPTS_PER_AGENT_FUNCTION = 5;

/** Per logical attempt: at most one upstream Gemini generation on the server. */
export const MAXIMUM_UPSTREAM_MODEL_ATTEMPTS_PER_LOGICAL_ATTEMPT =
  ORIGINAL_COMPAT_SERVER_UPSTREAM_ATTEMPTS_PER_LOGICAL_CALL;

/** Per golden agent function: logical max × upstream per attempt = 5. */
export const MAXIMUM_UPSTREAM_MODEL_ATTEMPTS_PER_AGENT_FUNCTION =
  GOLDEN_LOGICAL_MAX_ATTEMPTS_PER_AGENT_FUNCTION * MAXIMUM_UPSTREAM_MODEL_ATTEMPTS_PER_LOGICAL_ATTEMPT;

export type RetryLayerClassification =
  | 'ORIGINAL_LOGICAL_RETRY'
  | 'CLIENT_TRANSPORT_RETRY'
  | 'SERVER_TRANSPORT_RETRY'
  | 'UPSTREAM_MODEL_RETRY'
  | 'JSON_PARSE_RETRY';

export interface RetryLayerDescriptor {
  retryClass: RetryLayerClassification;
  location: string;
  maximumAttempts: number;
  triggerConditions: string[];
  logicalCallIdStable: boolean;
}

export const RETRY_LAYER_STACK: RetryLayerDescriptor[] = [
  {
    retryClass: 'ORIGINAL_LOGICAL_RETRY',
    location: 'goldenGenerateWithRetry.ts',
    maximumAttempts: GOLDEN_LOGICAL_MAX_ATTEMPTS_PER_AGENT_FUNCTION,
    triggerConditions: ['429', '500', '503', 'timeout', 'unavailable', 'high demand'],
    logicalCallIdStable: true,
  },
  {
    retryClass: 'JSON_PARSE_RETRY',
    location: 'goldenGenerateWithRetry.ts (parse failure inside logical loop)',
    maximumAttempts: GOLDEN_LOGICAL_MAX_ATTEMPTS_PER_AGENT_FUNCTION,
    triggerConditions: ['failed to parse ai response as json', 'empty response from ai'],
    logicalCallIdStable: true,
  },
  {
    retryClass: 'CLIENT_TRANSPORT_RETRY',
    location: 'geminiTransport.serverGenerateWithRetry (original_engine_compat policy)',
    maximumAttempts: ORIGINAL_COMPAT_CLIENT_HTTP_ATTEMPTS_PER_LOGICAL_CALL,
    triggerConditions: ['network error', '502', '503', '504 (compat: no retry — surfaces to logical owner)'],
    logicalCallIdStable: true,
  },
  {
    retryClass: 'SERVER_TRANSPORT_RETRY',
    location: 'server/routes/generate.ts (original_engine_compat policy)',
    maximumAttempts: 1,
    triggerConditions: ['N/A — serverRetryLimit forced to 1 for compat'],
    logicalCallIdStable: true,
  },
  {
    retryClass: 'UPSTREAM_MODEL_RETRY',
    location: 'server/services/geminiServer.generateWithRetry (original_engine_compat policy)',
    maximumAttempts: ORIGINAL_COMPAT_SERVER_UPSTREAM_ATTEMPTS_PER_LOGICAL_CALL,
    triggerConditions: ['N/A — maxRetries=1 for compat; 502 returns to client logical owner'],
    logicalCallIdStable: true,
  },
];

/** @deprecated use MAXIMUM_UPSTREAM_MODEL_ATTEMPTS_PER_LOGICAL_ATTEMPT */
export const MAXIMUM_UPSTREAM_ATTEMPTS_PER_LOGICAL_CALL = MAXIMUM_UPSTREAM_MODEL_ATTEMPTS_PER_LOGICAL_ATTEMPT;
/** @deprecated use ORIGINAL_COMPAT_CLIENT_HTTP_ATTEMPTS_PER_LOGICAL_CALL */
export const ORIGINAL_COMPAT_CLIENT_TRANSPORT_RETRIES_PER_LOGICAL_CALL =
  ORIGINAL_COMPAT_CLIENT_HTTP_ATTEMPTS_PER_LOGICAL_CALL;
